import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { firstValueFrom, race } from "rxjs";
import { filter, map, delay } from "rxjs/operators";
import { Auth } from "@angular/fire/auth";
import { AuthStoreDispatcher } from "../state/auth/auth.dispatcher";
import { ProfileStateDispatcher } from "../state/profile/profile.dispatcher";
import { environment } from "src/environments/environment";
import { doc, docData, Firestore } from "@angular/fire/firestore";
import { DEFAULT_DISMISS_AFTER_TIME, SopoPopupService } from "src/app/sopo-popup/service/sopo-popup.service";
import { AbstractClubJoinPopup } from "src/app/shared/components/abstract-club-join-popup/abstract-club-join-popup.component";
import { SopoDialogSettings } from "src/app/sopo-popup/interfaces/dialog";


@Injectable({
    providedIn: 'root'
})
export class NavigateClubGuard implements CanActivate {

    constructor(
        private router: Router,
        private auth: Auth,
        private authStoreDispatcher: AuthStoreDispatcher,
        private profileStateDispatcher: ProfileStateDispatcher,
        private firestore: Firestore,
        private sopoPopupService: SopoPopupService,
    ) {}

    async canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise < boolean | UrlTree > {
        //Check if the user accepted the terms(to not conflict with termsGuard)
        const profileState = await firstValueFrom(this.profileStateDispatcher.getProfileState());
        if (!profileState.profileData.termsAccepted && !profileState.termsAcceptedOnRegistration) {
            return true;
        }

        //Check if the user coming from login pages
        const navigation = this.router.getCurrentNavigation();
        if (!navigation.extras || !navigation.extras.state || !navigation.extras.state['fromLogin']) {
            return true;
        }

        //Check if the user is abstract user
        if (!this.auth.currentUser.isAnonymous && this.auth.currentUser.providerData.length === 0) {
            //Wait until the registration flag is not null, timeout is 3s
            const isRegistration$ = this.authStoreDispatcher.getState().pipe(
                map(authState => authState.isRegistration),
                filter(isRegistration => isRegistration != null)
            );
            const isRegistration = await firstValueFrom(isRegistration$);

            if (isRegistration) {
                //Wait till the user get in the club
                const popupOptions: SopoDialogSettings = {
                    backdropDismiss: false,
                    zIndex: 2002,
                };
                const popupRef = this.sopoPopupService.openDialog('app' , AbstractClubJoinPopup, popupOptions);
                const popupClose$ = popupRef.afterClosed.pipe(filter(closed => closed));

                const abstractClubId = environment.staging ? '5XbVvsZ4K5m8cyzEkqDz' : 'VFKo5oYzFm87WFlNKYZt';
                const userId = this.auth.currentUser.uid;
                const docRef = doc(this.firestore, `users/${userId}/clubs/${abstractClubId}`);
                const clubData$ = docData(docRef).pipe(
                    filter(doc => doc != null),
                    delay(DEFAULT_DISMISS_AFTER_TIME),
                );

                const clubData = await firstValueFrom(race(popupClose$, clubData$));

                if (clubData === true) {
                    return true;
                }

                popupRef.close();
                const url: UrlTree = this.router.parseUrl(`club-lobby/${abstractClubId}`);
                return url;
            }
        }
        return true;
    }
}
